import {Component} from '@angular/core';
import {Donation} from "../donation/donation";
import {OptionService} from "../option/option.service";

declare let Matomo: any;
declare let gtag: any;
declare let _trackerBaseUrl: any;

@Component({
  selector: 'tracking',
  templateUrl: './tracking.component.html'
})
export class TrackingComponent {
  /**
   * Status of a transaction
   */
  public static DONATION_STATUS_START = "0-started";
  public static DONATION_STATUS_SUCCESS = "3-successful";
  public static DONATION_STATUS_ERROR = "2-error";
  public static DONATION_STATUS_REDIRECT = "1-redirect";
  public static DONATION_STATUS_IFRAME = "1-iframe";
  public static DONATION_STATUS_CANCELED = "2-canceled";

  /**
   * Messages that can be send to the outside frame
   */
  public static MESSAGE_STEPCHANGE = "stepChange";
  public static MESSAGE_DONATION_STARTED = "donationStarted";
  public static MESSAGE_DONATION_FINISHED = "donationFinished";
  public static MESSAGE_VISIT = "firstInteraction";
  public static MESSAGE_SHOP_ADDTOCART = "addToCart";

  /**
   * Events for tracking the contact data
   */
  public static CONTACTDATA_SAVED = "3-contactdatasend";

  /**
   * Returns the Matomo tracker if it is available. Returns false if not
   *
   * @returns {object|boolean}
   */
  private static getTracker(): any {
    if (typeof Matomo !== "undefined") {
      return Matomo.getAsyncTracker();
    } else {
      return false;
    }
  }

  /**
   * Sent a message to the sorrounding frame (if there is one)
   *
   * @param message
   * @param values
   */
  public static sentMessage(message: string, values: any): void {
    // only send message if we are inside an iframe
    if (window.parent && window !== window.parent) {
      parent.postMessage({
        "type": message,
        "value": values,
        "cid": OptionService.getConatinerId()
      }, "*");
    }
  }

  /**
   * Tracks a first visit
   */
  public static visit(): void {
    TrackingComponent.sentMessage(TrackingComponent.MESSAGE_VISIT, {});
  }

  /**
   * Method that is called, every time a step is changed
   *
   * @param step
   */
  public static stepChanged(step: string): void {
    const tracker = TrackingComponent.getTracker();

    // send a message to the sorrounding frame
    TrackingComponent.sentMessage(TrackingComponent.MESSAGE_STEPCHANGE, {
      "step": step
    });

    let currentUrl = typeof _trackerBaseUrl !== "undefined" ? _trackerBaseUrl : window.location.pathname;
    currentUrl += "/" + step;

    // Twingle/Matomo Tracking
    if (tracker !== false) {
      tracker.trackPageView(currentUrl);

      if (step === "end") {
        tracker.trackGoal(2);
      }
    }

    // Google Analytics Tracking
    if (typeof gtag !== "undefined") {
      gtag("event", "page_view", {"page_location": currentUrl});

      // contact send
      if (step === "end") {
        gtag("event", "donation", {
          "event_category": "twingle",
          "event_label": TrackingComponent.CONTACTDATA_SAVED,
          "value": null
        });
      }
    }
  }

  /**
   * Method is called if the status of a donation changed
   *
   * @param {String} status
   * @param {Donation} donation
   */
  public static donationStatusChanged(status: string, donation: Donation): void {
    const tracker = TrackingComponent.getTracker();

    // send a message to the sorounding frame
    if (status === TrackingComponent.DONATION_STATUS_SUCCESS) {
      // add list of products to the data, if we have some
      let products = [];
      if (donation.products) {
        products = donation.products.map((product: any) => {
          return {
            product: product.product,
            internal_id: product.productInternalId ?? null,
            count: product.count,
            price: product.price
          }
        });
      }

      // sent message
      TrackingComponent.sentMessage(TrackingComponent.MESSAGE_DONATION_FINISHED, {
        "rythm": donation.rythm,
        "target": donation.target,
        "recurringRythm": donation.recurringRythm,
        "paymentMethod": donation.paymentMethod,
        "amount": donation.amount,
        "transaction_guid": donation.transactionGuid ?? null,
        "products": products
      });
    }

    if (status === TrackingComponent.DONATION_STATUS_START) {
      TrackingComponent.sentMessage(TrackingComponent.MESSAGE_DONATION_STARTED, {
        "rythm": donation.rythm,
        "target": donation.target,
        "recurringRythm": donation.recurringRythm,
        "paymentMethod": donation.paymentMethod,
        "amount": donation.amount
      });
    }

    // check if the donation includes contact data
    const donationHasConactData = donation.contactData &&
      donation.contactData.data &&
      donation.contactData.data.lastname &&
      donation.contactData.data.lastname.length > 0;

    if (tracker !== false) {
      tracker.trackEvent("donation-status", status);

      if (status === TrackingComponent.DONATION_STATUS_START) {
        // track used payment method
        tracker.trackEvent("payment-method", donation.paymentMethod);
        tracker.trackEvent("donation-rhytm", donation.rythm, donation.recurringRythm);
      }

      if (status === TrackingComponent.DONATION_STATUS_SUCCESS) {
        // count Target reach
        tracker.trackGoal(1, donation.amount);

        // check if the donation has also set the contact data
        if (donationHasConactData === true) {
          tracker.trackGoal(2);
        }
      }
    }

    // Google Analytics tracking of donation status change
    if (typeof gtag !== "undefined") {
      gtag("event", "donation", {
        "event_category": "twingle",
        "event_label": status,
        "value": (status === TrackingComponent.DONATION_STATUS_SUCCESS) ? donation.amount : null
      });

      // check if the donation has also set the contact data
      if (status === TrackingComponent.DONATION_STATUS_SUCCESS && donationHasConactData === true) {
        gtag("event", "donation", {
          "event_category": "twingle",
          "event_label": TrackingComponent.CONTACTDATA_SAVED,
          "value": null
        });
      }
    }
  }

  /**
   * Track an event
   */
  public static event(category: string, action: string, name: string): void {
    const tracker = this.getTracker();
    if (tracker) {
      tracker.trackEvent(category, action, name);
    }
  }

  /**
   * Constructor
   * @param optionService
   */
  constructor(protected optionService: OptionService) {}

}
